import type { routes, SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { isNil } from '@semios/app-platform-common'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { getMapOverlaysPadding } from 'App/Map/CurrentValuesMap/_utils/getMapOverlaysPadding'
import type { BlockScdsValuesCache } from 'App/Map/CurrentValuesMap/caches/BlockScdsValuesCache/BlockScdsValuesCache'
import type { BlocksDefaultPolygonsCache } from 'App/Map/CurrentValuesMap/caches/BlocksDefaultPolygonsCache/BlocksDefaultPolygonsCache'
import type { BlockValuesCache } from 'App/Map/CurrentValuesMap/caches/BlockValuesCache/BlockValuesCache'
import type { ImageOverlaysCache } from 'App/Map/CurrentValuesMap/caches/ImageOverlaysCache/ImageOverlaysCache'
import type { PropertyValuesCache } from 'App/Map/CurrentValuesMap/caches/PropertyValuesCache/PropertyValuesCache'
import type { StationValuesCache } from 'App/Map/CurrentValuesMap/caches/StationValuesCache/StationValuesCache'
import type { WindMachineValuesCache } from 'App/Map/CurrentValuesMap/caches/WindMachineValuesCache/WindMachineValuesCache'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import polylabel from 'polylabel'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { mapStore } from 'stores/mapStore/mapStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { withoutPrePostFilterEmitterTypes } from 'utils/isPrePostFilterEmitterType'
import { lngLatStringToLngLatObject } from 'utils/lngLatStringToLngLatObject'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { isRegionDataEnabled } from 'utils/useIsRegionDataEnabled'
import { getIdForLngLat } from '../CurrentValuesMap/_utils/getIdForLngLat'
import type { IrrigationFlowMeterValuesCache } from '../CurrentValuesMap/caches/IrrigationFlowMeterValuesCache/IrrigationFlowMeterValuesCache'
import type { IrrigationZonesPolygonsCache } from '../CurrentValuesMap/caches/IrrigationZonesPolygonsCache/IrrigationZonesPolygonsCache'
import type { IrrigationZoneValuesCache } from '../CurrentValuesMap/caches/IrrigationZoneValuesCache/IrrigationZoneValuesCache'
import type { RegionValuesCache } from '../CurrentValuesMap/caches/RegionsValuesCache/RegionsValuesCache'
import type { TrapValuesCache } from '../CurrentValuesMap/caches/TrapValuesCache/TrapValuesCache'

const euclideanDistance = <T extends { lat: number; lng: number }>(pointA: T, pointB: T) => {
  return Math.sqrt(Math.pow(pointA.lng - pointB.lng, 2) + Math.pow(pointA.lat - pointB.lat, 2))
}

export const populateMapFromUserAppStartup = (o: {
  maps: typeof google.maps
  map: google.maps.Map
  blocksDefaultPolygonsCache: BlocksDefaultPolygonsCache
  blockScdsValuesCache: BlockScdsValuesCache
  blockValuesCache: BlockValuesCache
  properties: routes.UserAppStartup.Response['properties']
  propertyValuesCache: PropertyValuesCache
  stationValuesCache: StationValuesCache
  windMachineValuesCache: WindMachineValuesCache
  trapValuesCache: TrapValuesCache
  imageOverlaysCache: ImageOverlaysCache
  irrigationZonePolygonsCache: IrrigationZonesPolygonsCache
  irrigationZoneValuesCache: IrrigationZoneValuesCache
  irrigationFlowMeterValuesCache: IrrigationFlowMeterValuesCache
  regions: routes.UserAppStartup.Response['regions']
  regionValuesCache: RegionValuesCache
}) => {
  // TODO: Note: all this should be doing is prepping the cache with location stuff: latLngs, bounds, etc.
  const allPropertyBounds = new o.maps.LatLngBounds()

  Object.entries(o.properties ?? {}).forEach(([propertyId, property]) => {
    const propertyBounds = new o.maps.LatLngBounds()

    if (property.blocks) {
      Object.values(property.blocks).forEach((block) => {
        const { blockId } = block
        const blockIdString = String(blockId)
        const { coordinates } = JSON.parse(block.geometry) as SharedTypes.TGeoJsonPolygon
        const blockBounds = new o.maps.LatLngBounds()

        const blockLatLngs = coordinates[0].map(([lng, lat]) => {
          blockBounds.extend({ lat, lng })

          return new o.maps.LatLng({ lat, lng })
        })

        const [lng, lat] = polylabel([coordinates[0]], 1.0)

        o.blockValuesCache.addItem({
          id: blockIdString,
          value: {},
          meta: {
            blockId: Number(blockId),
            blockName: block.name ?? translate.phrases.banyanApp('Unnamed Block'),
            bounds: blockBounds,
            latLng: new o.maps.LatLng({ lng, lat }),
            latLngs: blockLatLngs,
            propertyId: Number(propertyId),
          },
        })

        if (hubConnectorSettings.enableBlockPolygons) {
          o.blocksDefaultPolygonsCache.addItem({
            id: blockIdString,
            value: {},
            meta: {
              blockId,
              bounds: blockBounds,
              latLngs: blockLatLngs,
            },
          })
        }

        o.blockScdsValuesCache.addItem({
          id: blockId.toString(),
          meta: {
            bounds: blockBounds,
            blockId: blockIdString,
            scds: [],
          },
          value: {},
        })

        if (hubConnectorSettings.enableImageOverlays) {
          o.imageOverlaysCache.addItem({
            id: blockIdString,
            value: {},
            meta: {
              blockId: Number(blockIdString),
              blockName: block.name ?? translate.phrases.banyanApp('Unnamed Block'),
              bounds: blockBounds,
              latLngs: blockLatLngs,
              propertyId: Number(propertyId),
              latLng: new o.maps.LatLng({ lng, lat }),
            },
          })
        }

        blockLatLngs.forEach((latLng) => {
          propertyBounds.extend(latLng)

          allPropertyBounds.extend(latLng)
        })
      })
    }

    if (property.points) {
      Object.values(property.points).forEach((point) => {
        const { lngLat } = point
        const { lng, lat } = lngLatStringToLngLatObject(lngLat)
        const isTrap = Boolean(point.configuration.trapInsectIds)
        const isWindMachine = Boolean(point.valuesCurrent.some((value) => value.startsWith('windMachine_')))

        const irrigationZoneId = point.configuration.irrigationZoneId
          ? point.configuration.irrigationZoneId
          : undefined

        // a station's filters aren't something the map badges irrigation activity for
        const emitterTypes = withoutPrePostFilterEmitterTypes(
          point.configuration.irrigationEmitterTypesAvailable,
        )

        const isCumulativeFlowMeterValue = Boolean(
          point.valuesCurrent.some((value) => value.includes('cumulativeFlowMeter')),
        )

        const isOutOfBlock = point.valuesCurrent.some((vt) => vt.includes('_out_'))
        const latLng = new o.maps.LatLng({ lng, lat })
        const pointId = getIdForLngLat({ fieldAssetType: 'points', lngLat: lngLat })

        if (isTrap) {
          o.trapValuesCache.addItem({
            id: getIdForLngLat({ fieldAssetType: 'traps', lngLat: lngLat }),
            meta: {
              type: 'traps',
              insectId: point.configuration.trapInsectIds?.[0],
              propertyId: propertyId.toString(),
              blockId: point.blockIds?.[0],
              lngLat: lngLat,
              name: point.name ?? translate.phrases.banyanApp('Unnamed Trap'),
              latLng,
            },
            value: {},
          })
        }

        if (isWindMachine) {
          o.windMachineValuesCache.addItem({
            id: pointId,
            value: {},
            meta: {
              lngLat: lngLat,
              name: point.name ?? translate.phrases.banyanApp('Unnamed Wind Machine'),
              propertyId: property.propertyId,
              latLng,
              isOutOfBlock,
            },
          })
        }

        if (isCumulativeFlowMeterValue) {
          o.irrigationFlowMeterValuesCache.addItem({
            id: pointId,
            value: {},
            meta: {
              lngLat: lngLat,
              name: point.name ?? translate.phrases.banyanApp('Unnamed Flow Meter'),
              propertyId: property.propertyId,
              isOutOfBlock,
              latLng,
            },
          })
        }

        if (!isTrap && !isWindMachine && !isCumulativeFlowMeterValue) {
          o.stationValuesCache.addItem({
            id: pointId,
            value: {},
            meta: {
              lngLat: lngLat,
              name: point.name ?? translate.phrases.banyanApp('Unnamed Station'),
              propertyId: property.propertyId,
              blockId: point.blockIds?.[0],
              isOutOfBlock,
              latLng,
              irrigationZoneId,
              emitterTypes,
            },
          })
        } else if (!isTrap) {
          const hasStationValues = point.valuesCurrent.some(
            (value) => !value.startsWith('windMachine_') && !value.includes('cumulativeFlowMeter'),
          )

          if (hasStationValues) {
            o.stationValuesCache.addItem({
              id: pointId,
              value: {},
              meta: {
                lngLat: lngLat,
                name: point.name ?? translate.phrases.banyanApp('Unnamed Station'),
                propertyId: property.propertyId,
                blockId: point.blockIds?.[0],
                isOutOfBlock,
                latLng,
                irrigationZoneId,
                emitterTypes,
              },
            })
          }
        }
      })
    }

    if (property.irrigationZoneEmitters) {
      Object.entries(property.irrigationZoneEmitters).forEach(([zoneIdEmitterTypeKey, irrigationZone]) => {
        const { coordinates } = JSON.parse(irrigationZone.geometry) as SharedTypes.TGeoJsonMultiPolygon
        const irrigationZoneBounds = new o.maps.LatLngBounds()

        const irrigationZoneLatLngs = coordinates[0][0].map(([lng, lat]) => {
          irrigationZoneBounds.extend({ lat, lng })

          return new o.maps.LatLng({ lat, lng })
        })

        const [lng, lat] = polylabel([coordinates[0][0]], 1.0)

        o.irrigationZoneValuesCache.addItem({
          id: zoneIdEmitterTypeKey,
          value: {},
          meta: {
            irrigationZoneEmitterId: irrigationZone.irrigationZoneEmitterId,
            irrigationZoneEmitterType: irrigationZone.emitterType as TEmitterType,
            irrigationZoneName: irrigationZone.name ?? translate.phrases.banyanApp('Unnamed Zone'),
            irrigationZoneId: irrigationZone.irrigationZoneId,
            propertyId: property.propertyId,
            bounds: irrigationZoneBounds,
            latLng: new o.maps.LatLng({ lng, lat }),
            latLngs: irrigationZoneLatLngs,
            onClick: () =>
              setSelectedFieldAsset({
                irrigationZoneEmitter: irrigationZone.irrigationZoneEmitterId,
              }),
          },
        })

        if (hubConnectorSettings.enableIrrigationZonePolygons) {
          o.irrigationZonePolygonsCache.addItem({
            id: zoneIdEmitterTypeKey,
            value: {},
            meta: {
              irrigationZoneEmitterId: irrigationZone.irrigationZoneEmitterId,
              irrigationZoneEmitterType: irrigationZone.emitterType as TEmitterType,
              irrigationZoneId: irrigationZone.irrigationZoneId,
              propertyId: property.propertyId,
              bounds: irrigationZoneBounds,
              latLngs: irrigationZoneLatLngs,
              onClick: () =>
                setSelectedFieldAsset({
                  irrigationZoneEmitter: irrigationZone.irrigationZoneEmitterId,
                }),
            },
          })
        }
      })
    }

    if (hubConnectorSettings.enablePropertyValues) {
      o.propertyValuesCache.addItem({
        id: propertyId.toString(),
        value: {},
        meta: {
          propertyId: propertyId.toString(),
          propertyName: property.propertyName,
          bounds: propertyBounds,
        },
      })
    }
  })

  if (isRegionDataEnabled()) {
    Object.entries(o.regions ?? {}).forEach(([regionId, region]) => {
      const { labelLngLat } = region
      const { lng, lat } = lngLatStringToLngLatObject(labelLngLat)

      o.regionValuesCache.addItem({
        id: regionId,
        value: {},
        meta: {
          regionId: regionId,
          regionName: region.name,
          latLng: new o.maps.LatLng({ lat, lng }),
        },
      })
    })
  }

  // Have the map fit all the user's properties!
  if (!HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD && !mapStore.getState().bounds) {
    if (!isEmpty(o.properties)) {
      o.map.fitBounds(allPropertyBounds, getMapOverlaysPadding())
    } else {
      // flow for free users or users who don't have any properties assigned yet
      const Fresno = { lat: 36.7403, lng: -119.7883 }
      const currentLocation = mapStore.getState().currentLocation
      const initialLocation = Fresno
      const centre = currentLocation ? new o.maps.LatLng(currentLocation) : new o.maps.LatLng(initialLocation)
      const polygonThatsAGoodSize = new o.maps.Circle({ center: centre, radius: 100000 }).getBounds()
      const storedRegions = fieldAssetStore.getState().regions

      if (!isNil(storedRegions) && isNil(selectedFieldAssetsStore.getState().region)) {
        const regionalLngLats = Object.values(storedRegions).reduce(
          (acc: { lat: number; lng: number; regionId: string }[], region) => {
            if (!region?.labelLngLat) return acc

            const { lng, lat } = lngLatStringToLngLatObject(region.labelLngLat)

            const lngLatForRegion = {
              lng,
              lat,
              regionId: region.regionId,
            }

            acc.push(lngLatForRegion)

            return acc
          },
          [],
        )

        regionalLngLats.sort((a, b) => {
          return euclideanDistance(initialLocation, a) - euclideanDistance(initialLocation, b)
        })

        const nearestRegion = regionalLngLats[0]

        if (nearestRegion) setSelectedFieldAsset({ region: nearestRegion.regionId })
      }

      if (polygonThatsAGoodSize) o.map.fitBounds(polygonThatsAGoodSize, getMapOverlaysPadding())
    }
  }
}
